import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, finalize } from 'rxjs/operators';

import { ConfigService } from '../../_services/config.service';
import { SpinnerService } from '../../_services/SpinnerService';
import { UserNotify, UserNotifyType, NotificationFilter, NotifyBatchRequest, NotifyCampaign, NotifyCampaignRequest, NotifyCampaignContext, NotifyAudienceCount, NotifyPreference, NotifyChannel } from '../_models/notification.model';

@Injectable({
	providedIn: 'root',
})
export class NotificationsService {
	constructor(
		private http: HttpClient,
		private configService: ConfigService,
		private spinnerService: SpinnerService,
	) {}

	private get baseUrl(): string {
		return this.configService.portalUrl;
	}

	private formatDateParam(value: string | Date | undefined): string | null {
		if (!value) return null;
		if (value instanceof Date) {
			const y = value.getFullYear();
			const m = String(value.getMonth() + 1).padStart(2, '0');
			const d = String(value.getDate()).padStart(2, '0');
			return `${y}-${m}-${d}`;
		}
		return value;
	}

	GetNotifications(txtsearch: string = '', order_by: string = '', limit: number = 20, offset: number = 0, filters?: NotificationFilter): Observable<UserNotify[]> {
		this.spinnerService.show();

		let url = `${this.baseUrl}notify/list?txtsearch=${encodeURIComponent(txtsearch)}&orderby=${encodeURIComponent(order_by)}&limit=${limit}&offset=${offset}`;

		if (filters) {
			if (filters.usrnot_type) url += `&usrnot_type=${filters.usrnot_type}`;
			if (filters.usrnot_typesent) url += `&usrnot_typesent=${filters.usrnot_typesent}`;
			if (filters.status) url += `&status=${filters.status}`;
			const df = this.formatDateParam(filters.date_from);
			if (df) url += `&date_from=${df}`;
			const dt = this.formatDateParam(filters.date_to);
			if (dt) url += `&date_to=${dt}`;
			if (filters.campaign_id) url += `&campaign_id=${filters.campaign_id}`;
		}

		return this.http.get(url, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	GetNotification(usrnot_id: number): Observable<UserNotify> {
		this.spinnerService.show();

		return this.http.get(`${this.baseUrl}notify/get/${usrnot_id}`, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	InsertNotification(notification: Partial<UserNotify>): Observable<number> {
		this.spinnerService.show();

		return this.http.post(`${this.baseUrl}notify/add`, notification, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	DeleteNotification(usrnot_id: number): Observable<number> {
		this.spinnerService.show();

		return this.http.delete(`${this.baseUrl}notify/del/${usrnot_id}`, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	MarkAsRead(usrnot_id: number): Observable<number> {
		this.spinnerService.show();

		return this.http.put(`${this.baseUrl}notify/mark-read/${usrnot_id}`, {}, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	UpdateNotification(notification: Partial<UserNotify>): Observable<number> {
		this.spinnerService.show();

		return this.http.put(`${this.baseUrl}notify/upd`, notification, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	GetNotifyTypes(): Observable<UserNotifyType[]> {
		return this.http.get(`${this.baseUrl}notify/types`, { observe: 'response' }).pipe(map((response: any) => response.body));
	}

	GetUsersForNotify(fcmOnly: boolean = false): Observable<any[]> {
		const params = fcmOnly ? 'txtsearch=&fcm_only=true' : 'txtsearch=';
		return this.http.get(`${this.baseUrl}notify/users?${params}`, { observe: 'response' }).pipe(map((response: any) => response.body));
	}

	GetUnreadCount(): Observable<number> {
		return this.http.get(`${this.baseUrl}notify/unread-count`, { observe: 'response', headers: { 'X-No-Spinner': 'true' } }).pipe(map((response: any) => response.body));
	}

	/**
	 * Notifiche dell'utente corrente (solo le proprie, già inviate) per il pannello popover.
	 * Usa /my-list (scoped per utente lato backend) e nessuno spinner globale (background).
	 */
	GetRecentNotifications(limit: number = 15): Observable<UserNotify[]> {
		const url = `${this.baseUrl}notify/my-list?limit=${limit}&offset=0`;
		return this.http.get(url, { observe: 'response', headers: { 'X-No-Spinner': 'true' } }).pipe(map((response: any) => response.body));
	}

	/** Segna come letta senza spinner globale (usata dal pannello popover). */
	MarkAsReadSilent(usrnot_id: number): Observable<number> {
		return this.http.put(`${this.baseUrl}notify/mark-read/${usrnot_id}`, {}, { observe: 'response', headers: { 'X-No-Spinner': 'true' } }).pipe(map((response: any) => response.body));
	}

	/** Segna lette tutte le notifiche dell'utente corrente (scoped lato server). Ritorna quante. */
	MarkAllAsReadSilent(): Observable<number> {
		return this.http.put(`${this.baseUrl}notify/mark-all-read`, {}, { observe: 'response', headers: { 'X-No-Spinner': 'true' } }).pipe(map((response: any) => response.body));
	}

	/**
	 * Preferenze canale (email / notifica portale / niente) dell'utente corrente: stesso endpoint della
	 * sezione "Preferenze notifiche" del profilo, senza spinner (usato dal pannello popover).
	 */
	GetMyNotifyPreferencesSilent(cmpId: number): Observable<NotifyPreference[]> {
		return this.http.get(`${this.baseUrl}mail-settings/my?cmp_id=${cmpId || 0}`, { observe: 'response', headers: { 'X-No-Spinner': 'true' } }).pipe(map((response: any) => response.body));
	}

	/** Salva la scelta di canale per un singolo tipo evento (upsert lato server). */
	SaveMyNotifyPreferenceSilent(umst_id: number, channel: NotifyChannel): Observable<number> {
		return this.http.post(`${this.baseUrl}mail-settings/my`, [{ umst_id, ums_channel: channel }], { observe: 'response', headers: { 'X-No-Spinner': 'true' } }).pipe(map((response: any) => response.body));
	}

	RetryNotification(usrnot_id: number): Observable<number> {
		this.spinnerService.show();

		return this.http.put(`${this.baseUrl}notify/retry/${usrnot_id}`, {}, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	InsertBatchNotification(request: NotifyBatchRequest): Observable<number> {
		this.spinnerService.show();

		return this.http.post(`${this.baseUrl}notify/add-batch`, request, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	UploadNotificationImage(file: File): Observable<string> {
		this.spinnerService.show();
		const formData = new FormData();
		formData.append('file', file);

		return this.http.post(`${this.baseUrl}notify/upload-image`, formData, { observe: 'response', responseType: 'text' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	GetNotificationImageUrl(filename: string): string {
		return `${this.baseUrl}notify/image/${filename}`;
	}

	// ─── Campagne (invii massivi programmabili) ──────────────────────────────

	/** {is_admin, cmp_id}: decide quali audience mostrare nel wizard. */
	GetCampaignContext(): Observable<NotifyCampaignContext> {
		return this.http.get(`${this.baseUrl}notify/campaign/context`, { observe: 'response', headers: { 'X-No-Spinner': 'true' } }).pipe(map((response: any) => response.body));
	}

	/** Anteprima destinatari (senza spinner: si aggiorna mentre l'utente cambia i filtri). */
	CountCampaignAudience(request: Partial<NotifyCampaignRequest>): Observable<NotifyAudienceCount> {
		return this.http.post(`${this.baseUrl}notify/campaign/count`, request, { observe: 'response', headers: { 'X-No-Spinner': 'true' } }).pipe(map((response: any) => response.body));
	}

	CreateCampaign(request: NotifyCampaignRequest): Observable<{ unc_id: number; recipients: number }> {
		this.spinnerService.show();

		return this.http.post(`${this.baseUrl}notify/campaign`, request, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	GetCampaigns(txtsearch: string = '', state: string = '', date_from?: string | Date, date_to?: string | Date, limit: number = 20, offset: number = 0): Observable<NotifyCampaign[]> {
		this.spinnerService.show();

		let url = `${this.baseUrl}notify/campaign/list?txtsearch=${encodeURIComponent(txtsearch)}&state=${encodeURIComponent(state)}&limit=${limit}&offset=${offset}`;
		const df = this.formatDateParam(date_from);
		if (df) url += `&date_from=${df}`;
		const dt = this.formatDateParam(date_to);
		if (dt) url += `&date_to=${dt}`;

		return this.http.get(url, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	GetCampaign(unc_id: number): Observable<NotifyCampaign> {
		this.spinnerService.show();

		return this.http.get(`${this.baseUrl}notify/campaign/get/${unc_id}`, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	/** Annulla l'invio: cancella le righe non ancora inviate. Ritorna quante. */
	CancelCampaign(unc_id: number): Observable<number> {
		this.spinnerService.show();

		return this.http.put(`${this.baseUrl}notify/campaign/cancel/${unc_id}`, {}, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	GetCampaignRoles(cmp_id: number): Observable<{ rl_id: number; rl_name: string; rl_description: string }[]> {
		return this.http.get(`${this.baseUrl}notify/campaign/roles?cmp_id=${cmp_id}`, { observe: 'response', headers: { 'X-No-Spinner': 'true' } }).pipe(map((response: any) => response.body));
	}

	/** Solo ADMIN (403 altrimenti). */
	GetCampaignCompanies(): Observable<{ cmp_id: number; cmp_name: string }[]> {
		return this.http.get(`${this.baseUrl}notify/campaign/companies`, { observe: 'response', headers: { 'X-No-Spinner': 'true' } }).pipe(map((response: any) => response.body));
	}
}
